import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Megaphone,
  Plus,
  Send,
  Edit,
  Trash2,
  Loader2,
  Bell,
  Clock,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AnnouncementsPage = () => {
  const { api, isSuperAdmin, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const roles = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'auditor', label: 'Auditor' },
    { value: 'almacenista', label: 'Almacenista' },
    { value: 'user', label: 'Usuario' }
  ];

  const [form, setForm] = useState({
    title: '',
    message: '',
    type: 'motd',
    priority: 'normal',
    target_roles: [],
    duration_hours: null,
    is_active: true
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await api().get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAnnouncement(null);
    setForm({
      title: '',
      message: '',
      type: 'motd',
      priority: 'normal',
      target_roles: [],
      duration_hours: null,
      is_active: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setForm({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      priority: announcement.priority,
      target_roles: announcement.target_roles || [],
      duration_hours: announcement.duration_hours,
      is_active: announcement.is_active
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.message) {
      toast.error('Título y mensaje son requeridos');
      return;
    }

    setSaving(true);
    try {
      if (editingAnnouncement) {
        await api().put(`/announcements/${editingAnnouncement.id}`, form);
        toast.success('Anuncio actualizado');
      } else {
        await api().post('/announcements', form);
        toast.success('Anuncio creado');
      }
      setIsModalOpen(false);
      loadAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este anuncio?')) return;
    try {
      await api().delete(`/announcements/${id}`);
      toast.success('Anuncio eliminado');
      loadAnnouncements();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleBroadcast = async (id) => {
    try {
      await api().post(`/announcements/${id}/broadcast`);
      toast.success('Anuncio enviado a usuarios conectados');
    } catch (error) {
      toast.error('Error al enviar anuncio');
    }
  };

  const toggleRole = (roleValue) => {
    setForm(prev => {
      const roles = prev.target_roles.includes(roleValue)
        ? prev.target_roles.filter(r => r !== roleValue)
        : [...prev.target_roles, roleValue];
      return { ...prev, target_roles: roles };
    });
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-500/20 text-gray-400',
      normal: 'bg-blue-500/20 text-blue-400',
      high: 'bg-amber-500/20 text-amber-400',
      urgent: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      low: 'Baja',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return <Badge className={styles[priority]}>{labels[priority]}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'motd') {
      return <Badge className="bg-purple-500/20 text-purple-400">Mensaje del Día</Badge>;
    }
    return <Badge className="bg-cyan-500/20 text-cyan-400">Tiempo Real</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (!isSuperAdmin && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
          <p className="text-muted-foreground">
            Solo los administradores pueden acceder a esta sección.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-primary" />
            Mensaje del Día y Anuncios
          </h1>
          <p className="text-muted-foreground mt-1">
            Envía mensajes a los usuarios al iniciar sesión o en tiempo real
          </p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Anuncio
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <Bell className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mensajes del Día</p>
                <p className="text-2xl font-bold">
                  {announcements.filter(a => a.type === 'motd' && a.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-cyan-500/10 rounded-lg">
                <Send className="h-6 w-6 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tiempo Real</p>
                <p className="text-2xl font-bold">
                  {announcements.filter(a => a.type === 'realtime' && a.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-500/10 rounded-lg">
                <Clock className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Anuncios</p>
                <p className="text-2xl font-bold">{announcements.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Announcements Table */}
      <Card>
        <CardHeader>
          <CardTitle>Anuncios</CardTitle>
          <CardDescription>
            Lista de todos los mensajes y anuncios configurados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Sin Anuncios</h3>
              <p className="text-muted-foreground mb-4">
                Crea tu primer anuncio para comunicarte con los usuarios
              </p>
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Anuncio
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estado</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Destinatarios</TableHead>
                  <TableHead>Expira</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((announcement) => (
                  <TableRow key={announcement.id}>
                    <TableCell>
                      {announcement.is_active && !isExpired(announcement.expires_at) ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{announcement.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {announcement.message}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(announcement.type)}</TableCell>
                    <TableCell>{getPriorityBadge(announcement.priority)}</TableCell>
                    <TableCell>
                      {announcement.target_roles?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {announcement.target_roles.slice(0, 2).map(role => (
                            <Badge key={role} variant="outline" className="text-xs">
                              {role}
                            </Badge>
                          ))}
                          {announcement.target_roles.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{announcement.target_roles.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-xs">Todos</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {announcement.expires_at ? (
                        <span className={isExpired(announcement.expires_at) ? 'text-red-400' : ''}>
                          {formatDate(announcement.expires_at)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Sin expiración</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(announcement.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {announcement.type === 'realtime' && announcement.is_active && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleBroadcast(announcement.id)}
                            title="Enviar ahora"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(announcement)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(announcement.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Editar Anuncio' : 'Nuevo Anuncio'}
            </DialogTitle>
            <DialogDescription>
              {form.type === 'motd' 
                ? 'Este mensaje se mostrará como un modal al iniciar sesión'
                : 'Este mensaje se enviará en tiempo real como notificación toast'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ej: Mantenimiento Programado"
              />
            </div>

            <div className="space-y-2">
              <Label>Mensaje *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Escribe el contenido del anuncio..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Anuncio</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="motd">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Mensaje del Día (Modal)
                      </div>
                    </SelectItem>
                    <SelectItem value="realtime">
                      <div className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Tiempo Real (Toast)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Duración (horas)</Label>
              <Input
                type="number"
                min="1"
                value={form.duration_hours || ''}
                onChange={(e) => setForm({ ...form, duration_hours: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Dejar vacío para sin expiración"
              />
              <p className="text-xs text-muted-foreground">
                El anuncio expirará automáticamente después de este tiempo
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Destinatarios (Roles)
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Deja vacío para enviar a todos los usuarios
              </p>
              <div className="grid grid-cols-3 gap-2">
                {roles.map(role => (
                  <div
                    key={role.value}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      form.target_roles.includes(role.value)
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleRole(role.value)}
                  >
                    <Checkbox
                      checked={form.target_roles.includes(role.value)}
                      onCheckedChange={() => toggleRole(role.value)}
                    />
                    <span className="text-sm">{role.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Anuncio activo
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingAnnouncement ? 'Guardar Cambios' : 'Crear Anuncio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementsPage;
